// ============================================================================
// ROLE-BASED PERMISSIONS CONFIG
// Maps Keycloak roles to visible modules and allowed actions per module.
// Used at login time to filter navigation menu and module configs.
// ============================================================================

export interface RoleMenuConfig {
  visibleModules: string[];
  moduleActions: Record<string, string[]>;
}

// Keycloak role constants
export const ROLES = {
  SYSTEM: 'lmms_system',
  ADMIN: 'lmms_admin',
  APPROVER: 'lmms_approver',
  USER: 'lmms_user',
} as const;

export type AppRole = (typeof ROLES)[keyof typeof ROLES];

// All module IDs (must match keys in modulesConfig / nav item ids)
const ALL_MODULES = [
  'overview',
  'gold-rate-master',
  'customer-registry',
  'addon-requests',
  'loan-mappings',
  'closing-transactions',
  'combined-transactions',
  'regroup-transactions',
  'gl-replacements',
  'approval-dashboard',
  'report-addon-summary',
  'report-loan-mapping',
  'report-closing-settlement',
  'report-combined-txn',
  'report-gl-replacement',
  'report-audit-trail',
  'report-gold-rates',
  'system-config',
  'kfs-calculator',
  'entity-master',
  'business-unit-master',
  'user-bu-access',
  'role-permissions',
  'user-management',
];

// All possible actions across all modules
const ALL_ACTIONS = ['add', 'edit', 'delete', 'view', 'submit', 'activate', 'approve', 'return', 'reject', 'export', 'revoke', 'reset-password'];

// --------------------------------------------------------------------------
// Role → Permission Definitions
// --------------------------------------------------------------------------

export const ROLE_PERMISSIONS: Record<string, RoleMenuConfig> = {
  // Full unrestricted access
  [ROLES.SYSTEM]: {
    visibleModules: [...ALL_MODULES],
    moduleActions: Object.fromEntries(ALL_MODULES.map((m) => [m, [...ALL_ACTIONS]])),
  },

  // Admin: masters + config + administration + approvals + transactions + reports
  [ROLES.ADMIN]: {
    visibleModules: [
      'overview',
      'gold-rate-master',
      'customer-registry',
      'addon-requests',
      'loan-mappings',
      'closing-transactions',
      'combined-transactions',
      'regroup-transactions',
      'gl-replacements',
      'approval-dashboard',
      'report-addon-summary',
      'report-loan-mapping',
      'report-closing-settlement',
      'report-combined-txn',
      'report-gl-replacement',
      'report-audit-trail',
      'report-gold-rates',
      'system-config',
      'kfs-calculator',
      'entity-master',
      'business-unit-master',
      'user-bu-access',
      'role-permissions',
      'user-management',
    ],
    moduleActions: {
      'overview': ['view'],
      'gold-rate-master': ['add', 'edit', 'delete', 'activate'],
      'customer-registry': ['add', 'edit', 'delete'],
      'addon-requests': ['view'],
      'loan-mappings': ['view'],
      'closing-transactions': ['view'],
      'combined-transactions': ['view'],
      'regroup-transactions': ['view'],
      'gl-replacements': ['view'],
      'approval-dashboard': ['approve', 'return', 'reject'],
      'report-addon-summary': ['view', 'export'],
      'report-loan-mapping': ['view', 'export'],
      'report-closing-settlement': ['view', 'export'],
      'report-combined-txn': ['view', 'export'],
      'report-gl-replacement': ['view', 'export'],
      'report-audit-trail': ['view', 'export'],
      'report-gold-rates': ['view', 'export'],
      'system-config': ['edit'],
      'kfs-calculator': ['view'],
      'entity-master': ['add', 'edit', 'delete'],
      'business-unit-master': ['add', 'edit', 'delete'],
      'user-bu-access': ['add', 'edit', 'revoke'],
      'role-permissions': ['add', 'edit', 'delete', 'view'],
      'user-management': ['reset-password'],
    },
  },

  // Approver: dashboard + transactions (view) + approvals + reports
  [ROLES.APPROVER]: {
    visibleModules: [
      'overview',
      'addon-requests',
      'loan-mappings',
      'closing-transactions',
      'combined-transactions',
      'regroup-transactions',
      'gl-replacements',
      'approval-dashboard',
      'report-addon-summary',
      'report-loan-mapping',
      'report-closing-settlement',
      'report-combined-txn',
      'report-gl-replacement',
      'report-audit-trail',
      'report-gold-rates',
      'kfs-calculator',
    ],
    moduleActions: {
      'overview': ['view'],
      'addon-requests': ['view'],
      'loan-mappings': ['view'],
      'closing-transactions': ['view'],
      'combined-transactions': ['view'],
      'regroup-transactions': ['view'],
      'gl-replacements': ['view'],
      'approval-dashboard': ['approve', 'return', 'reject'],
      'report-addon-summary': ['view', 'export'],
      'report-loan-mapping': ['view', 'export'],
      'report-closing-settlement': ['view', 'export'],
      'report-combined-txn': ['view', 'export'],
      'report-gl-replacement': ['view', 'export'],
      'report-audit-trail': ['view', 'export'],
      'report-gold-rates': ['view', 'export'],
      'kfs-calculator': ['view'],
    },
  },

  // Branch user: dashboard + transactions (CRUD) + reports
  [ROLES.USER]: {
    visibleModules: [
      'overview',
      'addon-requests',
      'loan-mappings',
      'closing-transactions',
      'combined-transactions',
      'regroup-transactions',
      'gl-replacements',
      'report-addon-summary',
      'report-loan-mapping',
      'report-closing-settlement',
      'report-combined-txn',
      'report-gl-replacement',
      'report-audit-trail',
      'report-gold-rates',
      'kfs-calculator',
    ],
    moduleActions: {
      'overview': ['view'],
      'addon-requests': ['add', 'edit', 'delete', 'submit'],
      'loan-mappings': ['view'],
      'closing-transactions': ['add', 'edit', 'delete', 'submit'],
      'combined-transactions': ['add', 'edit', 'delete', 'submit'],
      'regroup-transactions': ['add', 'edit', 'delete', 'submit'],
      'gl-replacements': ['add', 'edit', 'delete', 'submit'],
      'report-addon-summary': ['view', 'export'],
      'report-loan-mapping': ['view', 'export'],
      'report-closing-settlement': ['view', 'export'],
      'report-combined-txn': ['view', 'export'],
      'report-gl-replacement': ['view', 'export'],
      'report-audit-trail': ['view', 'export'],
      'report-gold-rates': ['view', 'export'],
      'kfs-calculator': ['view'],
    },
  },
};

// --------------------------------------------------------------------------
// Helper: merge permissions for users with multiple roles (union)
// --------------------------------------------------------------------------

export function mergeRolePermissions(roles: string[]): RoleMenuConfig {
  const merged: RoleMenuConfig = {
    visibleModules: [],
    moduleActions: {},
  };

  const moduleSet = new Set<string>();

  for (const role of roles) {
    const config = ROLE_PERMISSIONS[role];
    if (!config) continue;

    for (const mod of config.visibleModules) {
      moduleSet.add(mod);
    }

    for (const [mod, actions] of Object.entries(config.moduleActions)) {
      if (!merged.moduleActions[mod]) {
        merged.moduleActions[mod] = [];
      }
      const existing = new Set(merged.moduleActions[mod]);
      for (const action of actions) {
        existing.add(action);
      }
      merged.moduleActions[mod] = [...existing];
    }
  }

  merged.visibleModules = [...moduleSet];
  return merged;
}

// --------------------------------------------------------------------------
// Helper: filter navigation menu based on visible modules
// --------------------------------------------------------------------------

interface NavItem {
  id: string;
  title: string;
  icon: string;
  active?: boolean;
}

interface NavSection {
  section: string;
  icon: string;
  items: NavItem[];
}

export function filterNavigationMenu(
  menu: NavSection[],
  visibleModules: string[]
): NavSection[] {
  const moduleSet = new Set(visibleModules);

  return menu
    .map((section) => ({
      ...section,
      items: section.items.filter((item) => moduleSet.has(item.id)),
    }))
    .filter((section) => section.items.length > 0);
}

// --------------------------------------------------------------------------
// Helper: override module actions based on role permissions
// --------------------------------------------------------------------------

export function applyRoleActions(
  modulesConfig: Record<string, any>,
  roleConfig: RoleMenuConfig
): Record<string, any> {
  const filtered: Record<string, any> = {};

  for (const moduleId of roleConfig.visibleModules) {
    if (modulesConfig[moduleId]) {
      filtered[moduleId] = {
        ...modulesConfig[moduleId],
        actions: roleConfig.moduleActions[moduleId] || [],
      };
    }
  }

  return filtered;
}
